import { a3Copy } from "@/components/assignments/a3-copy";
/**
 * The Hand in box in words (board 4). Every function reads the learner's own
 * attempts and their RELEASED judgements: a judgement not released is never
 * passed in, so a learner hand-in stays "Handed in" and an instructor record
 * stays named as a record. Nothing here compares a clock or ranks an act;
 * `pastDue` is the database's word and the filled act is the server's pick.
 */
import type { AssessmentKind, AssignmentDetail, AssignmentVersion, AttemptTerms, Feedback, LineFigure, ReleaseLine, ReleaseLinePerson, RubricCriterion, Submission } from "@/lib/api/assignments";
import { EXPECTED_DELIVERABLE_LABEL, latestAttempt } from "@/lib/assignment-presentation";
import { hasLevels, levelResults, levelWords, type LevelResult } from "@/components/assignments/work/rubric-levels";
import { formatInCourseZone } from "@/lib/session-portal";
import { zoneCity } from "@/lib/seat-workspace";

const DAY = { weekday: "short", month: "short", day: "numeric" } as const;
const DAY_TIME = { ...DAY, hour: "numeric", minute: "2-digit" } as const;

export const when = (instant: string, timezone: string) => formatInCourseZone(instant, timezone, DAY_TIME);
/** A moment a learner may need to prove: the day, the time and the course's zone. */
const zoned = (instant: string, timezone: string) => `${when(instant, timezone)} ${zoneCity(timezone)} time`;
/**
 * Why a published, open work refuses a hand-in because of its own deadline,
 * never "closed": closing is the teacher's separate act. Null when the window
 * is not what refuses it (closed, archived, or simply open).
 */
export function shutWords(terms: Pick<AssignmentDetail, "archived_at" | "status" | "window">, timezone: string, kept: "writing" | "entry" | null, now = Date.now()): string | null {
  const window = terms.window;
  if (terms.archived_at || terms.status === "closed" || window?.open !== false) return null;
  const saved = kept === "writing" ? " Your writing is saved." : kept === "entry" ? " Your entry is saved." : "";
  if (window.opens_at && (window.reason ? window.reason === "opens_later" : Date.parse(window.opens_at) > now)) return `This work opens for hand-ins ${zoned(window.opens_at, timezone)}.${saved}`;
  if (!window.due_at) return null;
  const lateUntil = window.accepts_until && Date.parse(window.accepts_until) > Date.parse(window.due_at) ? window.accepts_until : null;
  return lateUntil
    ? `The deadline was ${zoned(window.due_at, timezone)}, and late hand-ins were accepted until ${zoned(lateUntil, timezone)}.${saved} Ask your instructor about an extension.`
    : `The deadline was ${zoned(window.due_at, timezone)}. This work does not accept late hand-ins.${saved} Ask your instructor about an extension.`;
}

/** When an attempt arrived against the deadline it was admitted under. The
 * receipt keeps that deadline, so a later extension never rewrites it. */
export function admittedWords(attempt: Pick<Submission, "submitted_at" | "due_at_snapshot" | "late">, timezone: string): string {
  const arrived = `Handed in ${zoned(attempt.submitted_at, timezone)}.`;
  if (!attempt.due_at_snapshot) return arrived;
  return `${arrived} Your deadline for this attempt was ${zoned(attempt.due_at_snapshot, timezone)}. ${attempt.late ? "Late." : "On time."}`;
}
/** Numeric columns can arrive as text ("10.00"); a figure is said without trailing zeros. */
export const figure = (value: number | string) => Number(value).toLocaleString("en-US", { maximumFractionDigits: 2 });

/** The kind's name in the work line. */
export const KIND_WORDS: Record<AssessmentKind | "rubric", string> = {
  native_quiz: "Native quiz",
  scored: a3Copy.scored,
  rubric: a3Copy.scoredWithARubric,
  completion: a3Copy.completion,
  external_quiz: a3Copy.quizCompletedElsewhere,
  in_class: a3Copy.inClass,
};

/** Work written before the kinds has none; points make it scored, as the server's default does. */
export function kindOf(terms: Pick<AssignmentDetail, "assessment_kind" | "points">): AssessmentKind {
  return terms.assessment_kind ?? (terms.points != null ? "scored" : "completion");
}

/** One judgement, in the learner's words (LANGUAGE §4). */
export function outcomeWords(_kind: AssessmentKind, event: Feedback, points: number | null): string {
  if (event.outcome === "missing") return a3Copy.missing;
  if (event.outcome === "returned") return a3Copy.returnedForRevision;
  if (event.outcome === "complete") return a3Copy.complete;
  return event.score != null && points != null ? a3Copy.gradedOf(figure(event.score), figure(points)) : a3Copy.graded;
}

export interface HandInInput {
  terms: AssignmentDetail;
  attempts: Submission[];
  /** Released judgements by attempt id, oldest first. */
  released: Record<string, Feedback[]>;
  termOpen: boolean;
  timezone: string;
  /** "Session 9, Mon, Oct 19": where in-class work is assessed. */
  sessionWords?: string | null;
}

export interface HandInModel {
  kind: AssessmentKind;
  last: Submission | null;
  /** The newest released judgement of the latest attempt. */
  standing: Feedback | null;
  missing: boolean;
  /** The next attempt's number. */
  next: number;
  /** A further attempt carries its number in act names; one after a teacher's record does not. */
  numbered: boolean;
  mayWrite: boolean;
  /** A quiz completed elsewhere, not yet marked as taken, while it takes attempts. */
  takesMark: boolean;
  /** The box has an act. Whether it is the page's filled one is the server's word. */
  ranked: boolean;
  /** A released result with nothing more to hand in: the box folds to one line. */
  settled: boolean;
  word: string;
  line: string | null;
  /** Foot: what waits, beside the act. */
  waiting: string | null;
  actLabel: string;
  sheetTitle: string;
}

export function handInModel({ terms, attempts, released, termOpen, timezone, sessionWords }: HandInInput): HandInModel {
  const kind = kindOf(terms);
  const last = latestAttempt(attempts);
  const answeredKind = last?.attempt_terms?.assessment_kind ?? kind;
  const standing = last ? (released[last.id] ?? []).at(-1) ?? null : null;
  const missing = standing?.outcome === "missing";
  const recorded = last?.attempt_kind === "recorded";
  const taken = last?.attempt_kind === "attested";
  const open = terms.status === "published" && !terms.archived_at && terms.window?.open !== false && termOpen;
  const nothingToWrite = kind === "external_quiz" || kind === "in_class" || terms.expected_deliverable === "none";
  const mayWrite = open && !nothingToWrite && (!last || standing?.outcome === "returned" || missing);
  const takesMark = open && nothingToWrite && kind !== "in_class" && (!last || standing?.outcome === "returned" || missing);
  const next = (last?.attempt_number ?? 0) + 1;
  const numbered = Boolean(last && !recorded);
  const at = standing?.visible_at ? when(standing.visible_at, timezone) : "";

  let word: string;
  let line: string | null;
  if (!last) {
    word = kind === "external_quiz" ? a3Copy.notTaken : kind === "in_class" ? a3Copy.inClass : kind === "completion" && terms.expected_deliverable === "none" ? a3Copy.notMarkedDone : a3Copy.notHandedIn;
    line = kind === "in_class" ? [sessionWords, a3Copy.nothingToHandIn].filter(Boolean).join(" · ") : null;
  } else if (!standing) {
    word = recorded ? a3Copy.recordedByYourInstructor : taken ? (answeredKind === "external_quiz" ? a3Copy.taken : a3Copy.markedDone) : a3Copy.handedIn2;
    line = recorded || taken ? when(last.submitted_at, timezone) : a3Copy.attempt2(last.attempt_number, when(last.submitted_at, timezone), last.late ? " · Late" : "");
  } else if (standing.outcome === "returned") {
    word = a3Copy.returnedForRevision;
    line = a3Copy.attempt3(last.attempt_number, at);
  } else {
    word = outcomeWords(kind, standing, last.attempt_terms?.points ?? null);
    line = missing ? a3Copy.markedByYourInstructor(at)
      : recorded ? (answeredKind === "in_class" ? a3Copy.assessedInClass2(at) : a3Copy.recordedByYourInstructorNoHandIn)
      : taken ? a3Copy.attestationWhen(last.attempt_terms?.assessment_kind === "completion" ? a3Copy.markedDone : a3Copy.taken, when(last.submitted_at, timezone))
      : a3Copy.attempt3(last.attempt_number, at);
  }
  const closing = [terms.archived_at && "Archived", terms.window?.open === false && !terms.archived_at && terms.status !== "closed" && ((terms.window.reason ? terms.window.reason === "opens_later" : terms.window.opens_at && Date.parse(terms.window.opens_at) > Date.now()) ? "Not open yet" : "Deadline passed"), terms.status === "closed" && a3Copy.closedToNewAttempts, !termOpen && a3Copy.termClosed].filter(Boolean) as string[];
  if (closing.length) line = [line, ...closing].filter(Boolean).join(" · ");

  const settled = Boolean(standing && (standing.outcome === "graded" || standing.outcome === "complete")) && !mayWrite;
  return {
    kind, last, standing, missing, next, numbered, mayWrite, takesMark,
    ranked: mayWrite || takesMark,
    settled, word, line,
    waiting: takesMark ? (kind === "external_quiz" ? a3Copy.notMarkedAsTaken : a3Copy.notMarkedDone) : mayWrite ? (missing ? a3Copy.missingUntilYouHandItIn : a3Copy.attemptNotHandedIn(next)) : null,
    actLabel: numbered ? a3Copy.reviewAndHandInAttempt(next) : a3Copy.reviewAndHandIn,
    sheetTitle: recorded ? a3Copy.handIn : a3Copy.handInAttempt(next),
  };
}

/** `Due Thu, Oct 22, 10:54 AM · Past due · 20 points · Scored · Structured writing · Counts toward: Quizzes`. */
export function workLine(terms: Pick<AssignmentDetail, "due_at" | "window" | "points" | "rubric" | "expected_deliverable">, model: Pick<HandInModel, "kind" | "ranked">, opts: {
  timezone: string; pastDue: boolean; sessionWords?: string | null; lineLabel?: string | null;
}): string {
  const parts: string[] = [];
  const due = terms.window ? terms.window.due_at : terms.due_at;
  const pastDue = terms.window ? Boolean(due && Date.parse(due) < Date.now()) : opts.pastDue;
  if (model.kind !== "in_class") parts.push(due ? a3Copy.due2(when(due, opts.timezone)) : a3Copy.noDueDate);
  if (due && pastDue && model.ranked && model.kind !== "in_class") parts.push(a3Copy.pastDue);
  if (terms.points != null) parts.push(`${figure(terms.points)} ${Number(terms.points) === 1 ? "point" : "points"}`);
  parts.push(terms.rubric?.length ? KIND_WORDS.rubric : KIND_WORDS[model.kind]);
  if (terms.expected_deliverable && model.kind !== "external_quiz" && model.kind !== "in_class") parts.push(EXPECTED_DELIVERABLE_LABEL[terms.expected_deliverable]);
  if (opts.lineLabel) parts.push(a3Copy.countsToward(opts.lineLabel));
  return parts.join(" · ");
}

/** The line's name, when the learner's read carries it. */
export function lineName(line: LineFigure | null | undefined): string | null {
  return line?.label?.trim() || null;
}

/** "Your Quizzes so far: 87%, from 4 released scores · 1 not handed in, not counted yet". */
export function figureLine(line: LineFigure | null | undefined, label: string | null | undefined, withNotCounted: boolean): string | null {
  if (!line || line.mean == null) return null;
  const name = label ? a3Copy.yourSoFar(label) : a3Copy.soFarOnThisLine;
  const scores = a3Copy.released3(line.released_count, line.released_count === 1 ? "score" : "scores");
  const waiting = withNotCounted && line.not_handed_in_count > 0 ? a3Copy.notHandedInNotCountedYet2(line.not_handed_in_count) : "";
  return a3Copy.from(name, Math.round(Number(line.mean)), scores, waiting);
}

/** "Names the premise 3 of 3 · Cites the source 3 of 4", the rubric beside the grade; a level
 *  mark names each level, and an adjusted award its level's points and the marker's reason. */
export function criteriaLine(terms: Pick<AttemptTerms, "rubric">, event: Feedback): string | null {
  if (hasLevels(terms.rubric) && event.criterion_levels) return levelWords(terms.rubric!, event.criterion_levels).join(" · ") || null;
  if (!terms.rubric?.length || !event.criterion_scores) return null;
  const scores = event.criterion_scores;
  return terms.rubric.filter((item) => scores[item.id] != null).map((item) => a3Copy.of2(item.label, figure(scores[item.id]!), figure(item.points))).join(" · ") || null;
}

/** A level mark's criteria for the learner, one a line; null for any other judgement. */
export function levelCriteria(terms: Pick<AttemptTerms, "rubric">, event: Feedback): LevelResult[] | null {
  return hasLevels(terms.rubric) && event.criterion_levels ? levelResults(terms.rubric!, event.criterion_levels) : null;
}

/** The rubric in the brief: the frozen terms of the attempt handed in, or the work's current
 *  terms while a new attempt can be handed in, since that attempt answers them. */
export function briefRubric(terms: Pick<AssignmentDetail, "rubric">, model: Pick<HandInModel, "last" | "mayWrite" | "takesMark">): RubricCriterion[] | null {
  const rubric = model.last?.attempt_terms && !model.mayWrite && !model.takesMark ? model.last.attempt_terms.rubric : terms.rubric;
  return rubric?.length ? rubric : null;
}

/**
 * The terms changed after the learner last handed in, or after the box opened:
 * the newest version, its time, and what it changed in words.
 */
export function termsChange(versions: AssignmentVersion[], since: string | null, timezone: string): { at: string; words: string } | null {
  const ordered = [...versions].sort((a, b) => a.version_no - b.version_no);
  const newest = ordered.at(-1);
  const prior = (since ? ordered.filter((item) => Date.parse(item.created_at) <= Date.parse(since)).at(-1) : null) ?? ordered.at(-2);
  if (!newest || !prior || !since || Date.parse(newest.created_at) <= Date.parse(since)) return null;
  const changes: string[] = [];
  if (newest.due_at !== prior.due_at) changes.push(newest.due_at ? a3Copy.theDueTimeIsNow(when(newest.due_at, timezone)) : a3Copy.theDueTimeIsRemoved);
  if (String(newest.points) !== String(prior.points)) changes.push(newest.points != null ? a3Copy.thePointsAreNow(figure(newest.points)) : a3Copy.thePointsAreRemoved);
  if (newest.instructions !== prior.instructions) changes.push(a3Copy.theBriefChanged);
  if (newest.expected_deliverable !== prior.expected_deliverable) changes.push(a3Copy.whatYouHandInChanged);
  if (newest.assessment_kind !== prior.assessment_kind) changes.push(a3Copy.theAssessmentIsNow(KIND_WORDS[newest.assessment_kind ?? "scored"]));
  if (newest.external_url !== prior.external_url) changes.push(a3Copy.theQuizLinkChanged);
  if (JSON.stringify(newest.rubric) !== JSON.stringify(prior.rubric)) changes.push(a3Copy.theRubricChanged);
  return { at: when(newest.created_at, timezone), words: changes.join(", ") || a3Copy.theTermsWereRestated };
}

const personWords = (who: ReleaseLinePerson) => who.name ?? (who.role === "primary" ? "your instructor" : "a co-instructor");
const oneOf = (people: ReleaseLinePerson[]) => people.map(personWords).join(" or ");

/**
 * Who marks and who releases this work, in the learner's own view. It is built from staffing and release
 * grants only, so it reads the same from hand-in to release and never says a mark exists or is held.
 */
export function releaseLineWords(line: ReleaseLine | null | undefined): string | null {
  if (!line?.markers.length || !line.releasers.length) return null;
  const markers = oneOf(line.markers), releasers = oneOf(line.releasers);
  const words = markers === releasers ? `${markers} marks and releases it.` : `${markers} marks it; ${releasers} releases it.`;
  return words.charAt(0).toUpperCase() + words.slice(1);
}
